/**
 * RawanPlayer — مشغّل ذكي للهاتف فقط (Rawan UI).
 * - ووترمارك ثابت في الأعلى (مرئي دائماً).
 * - شريط تحكم زجاجي موحد (bg-black/40 backdrop-blur-sm).
 * - دعم وضع التضمين الذكي (Smart Embed) لمنصات Rumble و Vimeo و Facebook (YouTube عبر IFrame API).
 * - عزل تام للإطارات الخارجية (Sandbox) وفلترة الروابط (Clean URL) لمنع التشغيل التلقائي والمقترحات.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Play, Pause, Volume2, VolumeX, Volume1, Maximize2, SkipForward,
  ShieldCheck, Settings, X, Loader2, Camera, Download, Share2,
  ChevronLeft, ChevronRight, AlertTriangle, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { loadAdConfig, buildScheduleFromRule } from "@/lib/ad-config";
import { useDevtoolsOpen } from "@/lib/devtools-guard";
import { useTheme } from "@/lib/theme-context";
import { Slider } from "@/components/ui/slider";
import { createPortal } from "react-dom";

type Props = {
  url?: string;
  protectedUrl?: string;
  poster?: string;
  title?: string;
  ads?: boolean;
  kind?: "movie" | "series" | "trailer";
  onEnded?: () => void;
};

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (s: number, allow: boolean) => void;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  setVolume: (v: number) => void;
  getVolume: () => number;
  getPlayerState: () => number;
  destroy: () => void;
};

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.slice(7);
      if (u.pathname.startsWith("/shorts/")) return u.pathname.slice(8);
    }
    return null;
  } catch { return null; }
}

function isFacebookVideo(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (!(host.includes("facebook.com") || host === "fb.watch" || host.includes("fb.com"))) return false;
    // صفحة plugins جاهزة للتضمين
    if (u.pathname.startsWith("/plugins/video.php")) return true;
    // روابط فيديو شائعة
    if (/\/videos?\//.test(u.pathname)) return true;
    if (u.pathname.startsWith("/watch")) return true;
    if (u.pathname.startsWith("/reel/")) return true;
    if (host === "fb.watch") return true;
    return false;
  } catch { return false; }
}

function buildFacebookEmbedUrl(originalUrl: string): string {
  try {
    const u = new URL(originalUrl);
    if (u.pathname.startsWith("/plugins/video.php")) return originalUrl;
  } catch { /* ignore */ }
  const params = new URLSearchParams({
    href: originalUrl,
    show_text: "false",
    autoplay: "false",
    allowfullscreen: "true",
  });
  return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
}

function extractVimeoId(url: string): { id: string; hash?: string } | null {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("vimeo.com")) return null;
    // player.vimeo.com/video/123456?h=abcd
    if (u.hostname.startsWith("player.")) {
      const m = u.pathname.match(/^\/video\/(\d+)/);
      if (!m) return null;
      const hash = u.searchParams.get("h") || undefined;
      return { id: m[1], hash };
    }
    // vimeo.com/123456 or vimeo.com/123456/HASH
    const parts = u.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    if (/^\d+$/.test(parts[0])) {
      return { id: parts[0], hash: parts[1] && !/^\d+$/.test(parts[1]) ? parts[1] : undefined };
    }
    return null;
  } catch { return null; }
}

function isDirectVideo(url: string): boolean {
  return /\.(mp4|webm|m3u8|mkv|mov)(\?|$)/i.test(url);
}

function isHls(url: string): boolean {
  return /\.m3u8(\?|$)/i.test(url);
}

function isIframeHost(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  if (lower.includes("rumble.com/embed/")) return true;
  if (lower.includes("vimeo.com")) return true;
  if (isFacebookVideo(url)) return true;
  return false;
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve();
    if (window.YT && window.YT.Player) return resolve();
    const existing = document.getElementById("yt-iframe-api");
    if (!existing) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      s.async = true;
      document.head.appendChild(s);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(); };
    const iv = setInterval(() => {
      if (window.YT && window.YT.Player) { clearInterval(iv); resolve(); }
    }, 200);
    setTimeout(() => { clearInterval(iv); resolve(); }, 8000);
  });
}

function fmt(s: number) {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
}

type Shot = {
  id: number;
  time: number;
  baseDataUrl: string;
  W: number;
  H: number;
};

type ErrLogItem = {
  id: number;
  at: number;
  op: "save" | "share-native" | "share-whatsapp" | "share-telegram" | "capture" | "compose";
  reason: string;
  retry?: () => void;
};

const LS_PREFIX = "rawanui:player:";
const lsKey = (suffix: string, src: string) =>
  `${LS_PREFIX}${suffix}:${src.slice(0, 120)}`;

function readLS<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return JSON.parse(v) as T;
  } catch { return fallback; }
}
function writeLS(key: string, val: unknown) {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
}

export function RawanPlayer({ url, protectedUrl, poster, title, ads = true, kind, onEnded }: Props) {
  const { isAuthed, email } = useAuth();
  const isAdmin = isAuthed;
  const devtoolsOpen = useDevtoolsOpen();
  const { dir } = useTheme();
  const isRtl = dir === "rtl";

  const containerRef = useRef<HTMLDivElement | null>(null);
  const ytHostRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const hlsRef = useRef<any>(null);

  const [resolvedUrl, setResolvedUrl] = useState<string>(url ?? "");
  const [payloadKind, setPayloadKind] = useState<"movie" | "series" | "trailer" | undefined>(kind);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (protectedUrl) {
      import("@/lib/safelink-crypto").then(({ decryptInnerPayload }) =>
        decryptInnerPayload(protectedUrl)
          .then((p) => {
            if (cancelled) return;
            if (p.expired) {
              setError("انتهت صلاحية هذا الرابط — اطلب رابطاً جديداً من المسؤول.");
              return;
            }
            if (!p.url) { setError("الرابط المحمي غير صالح."); return; }
            setResolvedUrl(p.url);
            if (p.kind) setPayloadKind(p.kind as "movie" | "series" | "trailer");
          })
          .catch(() => {
            if (cancelled) return;
            setError("تعذّر فك تشفير المصدر — قد يكون الرابط تالفاً.");
          }),
      );
    } else {
      setResolvedUrl(url ?? "");
      if (kind) setPayloadKind(kind);
    }
    return () => { cancelled = true; };
  }, [url, protectedUrl, kind]);

  const ytId = useMemo(() => extractYouTubeId(resolvedUrl || ""), [resolvedUrl]);
  const isYT = !!ytId;
  const isEmbed = useMemo(() => isIframeHost(resolvedUrl), [resolvedUrl]);
  const direct = !isYT && !isEmbed && isDirectVideo(resolvedUrl || "");

  // معالجة رابط التضمين لتجريد واجهة المستخدم (UI) من أي إضافات خارجية
  const cleanEmbedUrl = useMemo(() => {
    if (!isEmbed || !resolvedUrl) return resolvedUrl;
    try {
      // Facebook: حوّل أي رابط فيديو إلى صفحة التضمين الرسمية
      if (isFacebookVideo(resolvedUrl)) {
        return buildFacebookEmbedUrl(resolvedUrl);
      }

      // Vimeo: أعد بناء الرابط على هيئة player.vimeo.com مع تجريد الواجهة
      const vimeo = extractVimeoId(resolvedUrl);
      if (vimeo) {
        const params = new URLSearchParams({
          autoplay: "0",
          dnt: "1",        // عدم التتبع
          badge: "0",      // إخفاء شارة Vimeo
          title: "0",      // إخفاء العنوان
          byline: "0",     // إخفاء اسم الناشر
          portrait: "0",   // إخفاء صورة الناشر
          pip: "0",        // تعطيل Picture-in-Picture
          playsinline: "1",
        });
        if (vimeo.hash) params.set("h", vimeo.hash);
        return `https://player.vimeo.com/video/${vimeo.id}?${params.toString()}`;
      }

      const u = new URL(resolvedUrl);

      // تقييد Rumble
      if (u.hostname.includes("rumble.com")) {
        u.searchParams.set("rel", "0");
      }

      // إعدادات عامة
      u.searchParams.set("autoplay", "0"); // منع التشغيل التلقائي
      return u.toString();
    } catch {
      return resolvedUrl;
    }
  }, [resolvedUrl, isEmbed]);

  const [phase, setPhase] = useState<"intro" | "playing" | "ad" | "outro" | "loading">("loading");
  const [adRemain, setAdRemain] = useState(0);
  const [adsServed, setAdsServed] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolumeState] = useState(80);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showErrLog, setShowErrLog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [shots, setShots] = useState<Shot[]>([]);
  const [openShot, setOpenShot] = useState<Shot | null>(null);
  const [shotMsg, setShotMsg] = useState<string | null>(null);
  const [shotMsgKind, setShotMsgKind] = useState<"ok" | "err">("ok");
  const [errLog, setErrLog] = useState<ErrLogItem[]>([]);
  const [thumbsVisible, setThumbsVisible] = useState(false);
  const [showShotsPanel, setShowShotsPanel] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);


  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showThumbsFor = useCallback((ms = 5000) => {
    setThumbsVisible(true);
    if (thumbsTimerRef.current) clearTimeout(thumbsTimerRef.current);
    thumbsTimerRef.current = setTimeout(() => setThumbsVisible(false), ms);
  }, []);

  const flash = useCallback((msg: string, kind: "ok" | "err" = "ok", ms = 1800) => {
    setShotMsg(msg); setShotMsgKind(kind);
    window.setTimeout(() => setShotMsg(null), ms);
  }, []);

  const logError = useCallback((op: ErrLogItem["op"], reason: string, retry?: () => void) => {
    setErrLog((prev) => {
      const next: ErrLogItem = { id: Date.now() + Math.random(), at: Date.now(), op, reason, retry };
      const arr = [next, ...prev].slice(0, 20);
      try { writeLS(`${LS_PREFIX}errlog`, arr.map(({ retry: _r, ...rest }) => rest)); } catch { /* ignore */ }
      return arr;
    });
  }, []);

  useEffect(() => {
    const saved = readLS<Omit<ErrLogItem, "retry">[]>(`${LS_PREFIX}errlog`, []);
    if (saved && saved.length) setErrLog(saved as ErrLogItem[]);
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 2800);
  }, []);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const schedule = useMemo(() => {
    if (!ads || !duration) return [];
    const cfg = loadAdConfig();
    const rule = cfg[(payloadKind ?? "movie") as "movie" | "series" | "trailer"];
    return buildScheduleFromRule(duration, rule).map((s) => ({ at: s.at, len: s.len }));
  }, [ads, duration, payloadKind]);

  useEffect(() => {
    if (!resolvedUrl) return;
    const saved = readLS<{ t?: number; muted?: boolean; volume?: number } | null>(
      lsKey("state", resolvedUrl),
      null,
    );
    if (saved) {
      if (typeof saved.volume === "number") setVolumeState(saved.volume);
      if (typeof saved.muted === "boolean") setMuted(saved.muted);
    }
  }, [resolvedUrl]);

  useEffect(() => {
    if (!resolvedUrl) return;
    let cancelled = false;
    setError(null);
    setPhase("loading");

    const savedState = readLS<{ t?: number; muted?: boolean; volume?: number } | null>(
      lsKey("state", resolvedUrl),
      null,
    );
    const resumeAt = savedState?.t && savedState.t > 5 ? savedState.t : 0;
    const initialMuted = savedState?.muted ?? false;
    const initialVolume = typeof savedState?.volume === "number" ? savedState.volume! : volume;

    (async () => {
      if (isYT) {
        await loadYouTubeAPI();
        if (cancelled || !ytHostRef.current || !window.YT) return;
        ytPlayerRef.current = new window.YT.Player(ytHostRef.current, {
          videoId: ytId!,
          host: "https://www.youtube-nocookie.com",
          playerVars: {
            modestbranding: 1, rel: 0, controls: 0, disablekb: 1,
            iv_load_policy: 3, fs: 0, playsinline: 1, origin: window.location.origin,
            start: Math.floor(resumeAt),
          },
          events: {
            onReady: (e: any) => {
              if (cancelled) return;
              setDuration(e.target.getDuration() || 0);
              try {
                e.target.setVolume(initialVolume);
                if (initialMuted) e.target.mute(); else e.target.unMute();
              } catch { /* ignore */ }
              setPhase("intro");
            },
            onStateChange: (e: any) => {
              if (e.data === 1) setIsPaused(false);
              else if (e.data === 2) setIsPaused(true);
              if (e.data === 0) { setPhase("outro"); setTimeout(() => onEnded?.(), 3000); }
            },
            onError: () => setError("تعذّر تحميل الفيديو"),
          },
        }) as unknown as YTPlayer;
      } else if (isEmbed) {
        // حالة التضمين الخارجي
        setPhase("intro");
      } else if (direct) {
        const v = videoRef.current;
        if (!v) { setPhase("intro"); return; }
        v.muted = initialMuted;
        v.volume = Math.max(0, Math.min(1, initialVolume / 100));
        const onLoaded = () => {
          if (resumeAt > 0 && videoRef.current) {
            try { videoRef.current.currentTime = resumeAt; } catch { /* ignore */ }
          }
          videoRef.current?.removeEventListener("loadedmetadata", onLoaded);
        };
        v.addEventListener("loadedmetadata", onLoaded);

        if (isHls(resolvedUrl)) {
          // HLS: استخدم hls.js إن لم يكن المتصفح يدعم HLS أصلياً (Safari).
          const canNative = v.canPlayType("application/vnd.apple.mpegurl");
          if (canNative) {
            v.crossOrigin = "anonymous";
            v.src = resolvedUrl;
            v.load();
            setPhase("intro");
          } else {
            try {
              const Hls = (await import("hls.js")).default;
              if (cancelled) return;
              if (Hls.isSupported()) {
                const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
                hlsRef.current = hls;
                hls.loadSource(resolvedUrl);
                hls.attachMedia(v);
                hls.on(Hls.Events.ERROR, (_e: any, data: any) => {
                  if (data?.fatal) setError("تعذّر تحميل بث HLS — تحقق من الرابط.");
                });
                setPhase("intro");
              } else {
                setError("متصفحك لا يدعم تشغيل HLS.");
              }
            } catch {
              setError("تعذّر تحميل مشغّل HLS.");
            }
          }
        } else {
          // MP4/WebM/إلخ — بث تدريجي مباشر (Range Requests) لتشغيل فوري
          // بدون انتظار تنزيل الملف كاملاً. crossOrigin مطلوب للسماح بالتقاط
          // اللقطات (canvas.drawImage) دون تلويث CORS.
          try {
            v.crossOrigin = "anonymous";
            v.preload = "auto";
            v.src = resolvedUrl;
            v.load();
            setPhase("intro");
          } catch {
            setError("تعذّر تحميل الفيديو.");
          }
        }
      } else {
        setError("نوع المصدر غير مدعوم في المشغل الذكي.");
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (resolvedUrl) {
          const cur = isYT
            ? (ytPlayerRef.current?.getCurrentTime?.() ?? progress)
            : (videoRef.current?.currentTime ?? progress);
          writeLS(lsKey("state", resolvedUrl), { t: cur, muted, volume });
        }
      } catch { /* ignore */ }
      try { ytPlayerRef.current?.destroy(); } catch { /* ignore */ }
      ytPlayerRef.current = null;
      try { hlsRef.current?.destroy?.(); } catch { /* ignore */ }
      hlsRef.current = null;
      // تنظيف عنصر الفيديو لمنع تسرّب الذاكرة عند تغيير المصدر/الإغلاق.
      if (videoRef.current) {
        try { videoRef.current.pause(); } catch { /* ignore */ }
        try { videoRef.current.removeAttribute("src"); videoRef.current.load(); } catch { /* ignore */ }
      }
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    };
  }, [resolvedUrl, ytId, isYT, isEmbed, direct, onEnded]);

  useEffect(() => {
    if (phase !== "intro") return;
    // شعار Rawan UI السريع: 800ms فقط بدل 2500ms — تشغيل شبه فوري.
    const tm = setTimeout(() => {
      setPhase("playing");
      try {
        if (isYT) ytPlayerRef.current?.playVideo();
        else if (!isEmbed) videoRef.current?.play().catch(() => { /* ignore */ });
      } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(tm);
  }, [phase, isYT, isEmbed]);

  useEffect(() => {
    if (phase !== "playing" || isEmbed) return;
    const iv = setInterval(() => {
      let cur = 0, dur = 0;
      if (isYT && ytPlayerRef.current) {
        cur = ytPlayerRef.current.getCurrentTime();
        dur = ytPlayerRef.current.getDuration();
      } else if (videoRef.current) {
        cur = videoRef.current.currentTime;
        dur = videoRef.current.duration || 0;
      }
      if (dur && dur !== duration) setDuration(dur);
      setProgress(cur);
      if (resolvedUrl && Math.floor(cur) % 3 === 0) {
        writeLS(lsKey("state", resolvedUrl), { t: cur, muted, volume });
      }
      for (const slot of schedule) {
        if (adsServed.has(slot.at)) continue;
        if (cur >= slot.at && cur <= slot.at + 2) { triggerAd(slot.len, slot.at); break; }
      }
    }, 500);
    return () => clearInterval(iv);
  }, [phase, isYT, isEmbed, schedule, adsServed, duration, resolvedUrl, muted, volume]);

  useEffect(() => {
    if (isEmbed) return;
    const save = () => {
      if (!resolvedUrl) return;
      const cur = isYT
        ? (ytPlayerRef.current?.getCurrentTime?.() ?? progress)
        : (videoRef.current?.currentTime ?? progress);
      writeLS(lsKey("state", resolvedUrl), { t: cur, muted, volume });
    };
    const onVis = () => { if (document.visibilityState === "hidden") save(); };
    window.addEventListener("pagehide", save);
    window.addEventListener("beforeunload", save);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", save);
      window.removeEventListener("beforeunload", save);
      document.removeEventListener("visibilitychange", onVis);
      save();
    };
  }, [resolvedUrl, isYT, isEmbed, progress, muted, volume]);

  useEffect(() => {
    if (!resolvedUrl || isEmbed) return;
    writeLS(lsKey("state", resolvedUrl), { t: progress, muted, volume });
  }, [muted, volume, isEmbed]);

  function triggerAd(len: number, slotKey: number) {
    if (isEmbed) return;
    setAdRemain(len);
    setPhase("ad");
    setAdsServed((prev) => new Set(prev).add(slotKey));
    try {
      if (isYT) ytPlayerRef.current?.pauseVideo();
      else videoRef.current?.pause();
    } catch { /* ignore */ }
    const iv = setInterval(() => {
      setAdRemain((r) => {
        if (r <= 1) {
          clearInterval(iv);
          setPhase("playing");
          try {
            if (isYT) ytPlayerRef.current?.playVideo();
            else videoRef.current?.play().catch(() => { /* ignore */ });
          } catch { /* ignore */ }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  }

  function togglePlay() {
    if (phase === "ad" || isEmbed) return;
    if (isYT && ytPlayerRef.current) {
      const st = ytPlayerRef.current.getPlayerState();
      if (st === 1) ytPlayerRef.current.pauseVideo();
      else ytPlayerRef.current.playVideo();
    } else if (videoRef.current) {
      if (videoRef.current.paused) videoRef.current.play().catch(() => { /* ignore */ });
      else videoRef.current.pause();
    }
  }

  function toggleMute() {
    if (isEmbed) return;
    if (isYT && ytPlayerRef.current) {
      if (ytPlayerRef.current.isMuted()) { ytPlayerRef.current.unMute(); setMuted(false); }
      else { ytPlayerRef.current.mute(); setMuted(true); }
    } else if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
    }
  }

  function applyVolume(v: number) {
    if (isEmbed) return;
    const clamped = Math.max(0, Math.min(100, v));
    setVolumeState(clamped);
    if (clamped === 0) {
      if (isYT) ytPlayerRef.current?.mute();
      if (videoRef.current) videoRef.current.muted = true;
      setMuted(true);
    } else {
      if (muted) {
        if (isYT) ytPlayerRef.current?.unMute();
        if (videoRef.current) videoRef.current.muted = false;
        setMuted(false);
      }
      if (isYT) ytPlayerRef.current?.setVolume(clamped);
      if (videoRef.current) videoRef.current.volume = clamped / 100;
    }
  }

  function seekBy(delta: number) {
    if (phase === "ad" || isEmbed) return;
    if (isYT && ytPlayerRef.current) {
      const t = ytPlayerRef.current.getCurrentTime() + delta;
      ytPlayerRef.current.seekTo(Math.max(0, t), true);
    } else if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime + delta);
    }
  }
  function seekTo(t: number) {
    if (phase === "ad" || isEmbed) return;
    if (isYT && ytPlayerRef.current) ytPlayerRef.current.seekTo(t, true);
    else if (videoRef.current) videoRef.current.currentTime = t;
  }
  function goFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    // Detect coarse pointer (phone/tablet) — desktop stays as-is.
    const isTouch = typeof window !== "undefined" &&
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      try {
        // Release orientation lock when leaving fullscreen.
        // Some browsers expose `unlock()` only on the ScreenOrientation instance.
        (screen.orientation as any)?.unlock?.();
      } catch { /* ignore */ }
    } else {
      const req = el.requestFullscreen?.();
      Promise.resolve(req).then(() => {
        if (isTouch) {
          try {
            (screen.orientation as any)?.lock?.("landscape").catch(() => {});
          } catch { /* orientation lock unsupported */ }
        }
      }).catch(() => {});
    }
  }

  // Auto-release orientation lock when the user exits fullscreen via ESC / system UI.
  useEffect(() => {
    function onFsChange() {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);
      if (!isFs) {
        try { (screen.orientation as any)?.unlock?.(); } catch { /* ignore */ }
      }
    }
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  function composeShot(base: { baseDataUrl: string; W: number; H: number; time: number }): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const { W, H } = base;
          const canvas = document.createElement("canvas");
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("no ctx"));

          ctx.drawImage(img, 0, 0, W, H);

          // مقياس ديناميكي بحسب دقة الفيديو (720p ≈ 1.0)
          const scale = Math.max(1, W / 720);
          const pad = Math.round(16 * scale);
          const bh = Math.round(44 * scale);
          const iconSize = Math.round(bh * 0.55);
          const gap = Math.round(8 * scale);
          const innerPadX = Math.round(14 * scale);

          // تحضير النص لقياس عرض القرص (يتغيّر بحسب اتجاه اللغة)
          const fontSize = Math.round(bh * 0.42);
          ctx.font = `900 ${fontSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
          const rawanW = ctx.measureText("Rawan").width;
          const uiW = ctx.measureText("UI").width;
          const rawanArW = ctx.measureText("روان").width;
          const uiArW = ctx.measureText(" UI").width;
          const textW = isRtl ? (rawanArW + uiArW) : (rawanW + uiW);
          const bw = Math.round(innerPadX * 2 + iconSize + gap + textW);
          const radius = bh / 2;
          const bx = W - pad - bw;
          const by = pad;

          // خلفية زجاجية مع حد أبيض خافت
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.beginPath();
          if ((ctx as any).roundRect) (ctx as any).roundRect(bx, by, bw, bh, radius);
          else ctx.rect(bx, by, bw, bh);
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.22)";
          ctx.lineWidth = Math.max(1, 1.5 * scale);
          ctx.stroke();

          // رسم أيقونة الدرع الحقيقية (Lucide ShieldCheck) من SVG — ليست إيموجي
          const iconColor = "#299e94";
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>`;
          const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
          const iconImg = new Image();
          await new Promise<void>((res, rej) => {
            iconImg.onload = () => res();
            iconImg.onerror = () => rej(new Error("shield icon load failed"));
            iconImg.src = svgUrl;
          });
          const iconY = by + (bh - iconSize) / 2;

          const textY = by + bh / 2;
          ctx.textBaseline = "middle";
          ctx.textAlign = "left";

          if (isRtl) {
            // الترتيب البصري من اليسار لليمين: [الدرع] -> [مسافة] -> [UI] -> [مسافة] -> [روان]
            const iconX = bx + innerPadX;
            ctx.drawImage(iconImg, iconX, iconY, iconSize, iconSize);
            
            const uiTextX = iconX + iconSize + gap;
            ctx.fillStyle = "#299e94";
            ctx.fillText("UI", uiTextX, textY);
            
            const rawanTextX = uiTextX + uiArW;
            ctx.fillStyle = "#ffffff";
            ctx.fillText("روان", rawanTextX, textY);
          } else {
            // الترتيب البصري من اليسار لليمين: [Rawan] -> [UI] -> [مسافة] -> [الدرع]
            const textStartX = bx + innerPadX;
            ctx.fillStyle = "#ffffff";
            ctx.fillText("Rawan", textStartX, textY);
            
            const uiTextX = textStartX + rawanW;
            ctx.fillStyle = "#299e94";
            ctx.fillText("UI", uiTextX, textY);
            
            const iconX = uiTextX + uiW + gap;
            ctx.drawImage(iconImg, iconX, iconY, iconSize, iconSize);
          }

          // شارة التوقيت أسفل يسار
          const tsBh = Math.round(36 * scale);
          const tsFont = Math.round(tsBh * 0.5);
          ctx.font = `bold ${tsFont}px ui-monospace, SFMono-Regular, Menlo, monospace`;
          const tsText = fmt(base.time);
          const tsBw = Math.round(ctx.measureText(tsText).width + tsBh);
          const tsY = H - pad - tsBh;
          ctx.fillStyle = "rgba(0,0,0,0.7)";
          ctx.beginPath();
          if ((ctx as any).roundRect) (ctx as any).roundRect(pad, tsY, tsBw, tsBh, tsBh / 2);
          else ctx.rect(pad, tsY, tsBw, tsBh);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(tsText, pad + tsBw / 2, tsY + tsBh / 2);

          resolve(canvas.toDataURL("image/jpeg", 0.98));
        } catch (e) { reject(e); }
      };
      img.onerror = () => reject(new Error("image load failed"));
      img.src = base.baseDataUrl;
    });
  }

  async function captureScreenshot(silent = false): Promise<Shot | null> {
    if (isYT || isEmbed) {
      const reason = "لا يمكن التقاط صور من مصادر Iframe الخارجية بسبب سياسة الأمان (CORS).";
      if (!silent) flash("الالتقاط غير مدعوم لهذه المنصة.", "err", 2800);
      logError("capture", reason, () => captureScreenshot(false));
      return null;
    }
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      const reason = "الفيديو لم يُحمَّل بعد (videoWidth=0).";
      if (!silent) flash("جاري تحميل الفيديو — أعد المحاولة بعد قليل.", "err", 2500);
      logError("capture", reason, () => captureScreenshot(false));
      return null;
    }
    try {
      const W = v.videoWidth, H = v.videoHeight;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no canvas context");
      ctx.drawImage(v, 0, 0, W, H);
      // PNG بدون فقدان — يمنع فقدان الجودة عند التركيب لاحقاً في composeShot
      const baseDataUrl = canvas.toDataURL("image/png");
      const shot: Shot = { id: Date.now(), time: v.currentTime, baseDataUrl, W, H };
      setShots((s) => [...s, shot].slice(-9));
      showThumbsFor(5000);
      if (!silent) flash("تم التقاط اللقطة", "ok", 1400);
      return shot;
    } catch (e: any) {
      const reason = e?.message || "خطأ غير معروف (قد يكون المصدر محمياً بـ CORS).";
      if (!silent) flash("تعذّر التقاط اللقطة (CORS).", "err", 2800);
      logError("capture", reason, () => captureScreenshot(false));
      return null;
    }
  }

  async function downloadShot(s: Shot) {
    let dataUrl = "";
    try {
      dataUrl = await composeShot(s);
    } catch (e: any) {
      logError("compose", e?.message || "تعذّر تركيب الصورة", () => downloadShot(s));
      flash("تعذّر تحضير الصورة.", "err", 2400);
      return;
    }
    try {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `rawanui-shot-${s.id}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      flash("تم حفظ اللقطة", "ok", 1400);
    } catch (e: any) {
      logError("save", e?.message || "فشل تنزيل الملف", () => downloadShot(s));
      flash("تعذّر حفظ اللقطة.", "err", 2400);
    }
  }

  async function shareShot(s: Shot, target?: "whatsapp" | "telegram" | "native") {
    const op: ErrLogItem["op"] = target === "whatsapp" ? "share-whatsapp"
      : target === "telegram" ? "share-telegram" : "share-native";
    let dataUrl = "";
    try {
      dataUrl = await composeShot(s);
    } catch (e: any) {
      logError("compose", e?.message || "تعذّر تركيب الصورة", () => shareShot(s, target));
      flash("تعذّر تحضير الصورة للمشاركة.", "err", 2400);
      return;
    }
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `rawanui-shot-${s.id}.jpg`, { type: "image/jpeg" });
      const text = `لقطة من ${title ?? "RawanUI"} عند ${fmt(s.time)}`;
      const navAny = navigator as any;
      if ((target === "native" || !target) && navAny.canShare?.({ files: [file] })) {
        try {
          await navAny.share({ files: [file], title: title ?? "RawanUI", text });
          return;
        } catch (e: any) {
          if (e?.name === "AbortError") return;
          logError(op, `Web Share API فشل: ${e?.name ?? ""} ${e?.message ?? ""}`.trim(),
            () => shareShot(s, target));
          flash("تعذّرت المشاركة الأصلية — تم تنزيل الصورة بدلاً من ذلك.", "err", 2400);
        }
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `rawanui-shot-${s.id}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      const enc = encodeURIComponent(text + " — RawanUI");
      if (target === "whatsapp") {
        const w = window.open(`https://wa.me/?text=${enc}`, "_blank", "noopener");
        if (!w) {
          logError("share-whatsapp", "نافذة منبثقة محجوبة من المتصفح", () => shareShot(s, "whatsapp"));
          flash("امنح إذن النوافذ المنبثقة لفتح واتساب.", "err", 2400);
        } else flash("تم تنزيل الصورة — أرفقها يدوياً في واتساب.", "ok", 2400);
      } else if (target === "telegram") {
        const w = window.open(`https://t.me/share/url?url=${encodeURIComponent(location.href)}&text=${enc}`, "_blank", "noopener");
        if (!w) {
          logError("share-telegram", "نافذة منبثقة محجوبة من المتصفح", () => shareShot(s, "telegram"));
          flash("امنح إذن النوافذ المنبثقة لفتح تلجرام.", "err", 2400);
        } else flash("تم تنزيل الصورة — أرفقها يدوياً في تلجرام.", "ok", 2400);
      } else {
        flash("المشاركة الأصلية غير مدعومة — تم تنزيل الصورة.", "ok", 2400);
      }
    } catch (e: any) {
      logError(op, e?.message || "خطأ مشاركة غير متوقع", () => shareShot(s, target));
      flash("تعذّرت المشاركة — جرّب التنزيل ثم المشاركة يدوياً.", "err", 2800);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEmbed) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (!containerRef.current) return;
      switch (e.key) {
        case " ":
        case "k":
        case "K":
          e.preventDefault(); togglePlay(); showControls(); break;
        case "m":
        case "M":
          e.preventDefault(); toggleMute(); showControls(); break;
        case "ArrowRight":
          e.preventDefault(); seekBy(10); showControls(); break;
        case "ArrowLeft":
          e.preventDefault(); seekBy(-10); showControls(); break;
        case "s":
        case "S":
          e.preventDefault(); captureScreenshot(); break;
        case "f":
        case "F":
          e.preventDefault(); goFullscreen(); break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, isYT, isEmbed, muted, volume]);

  useEffect(() => {
    if (!devtoolsOpen) return;
    try { ytPlayerRef.current?.destroy(); } catch { /* ignore */ }
    ytPlayerRef.current = null;
    try { hlsRef.current?.destroy?.(); } catch { /* ignore */ }
    hlsRef.current = null;
    if (ytHostRef.current) ytHostRef.current.innerHTML = "";
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { /* ignore */ }
      videoRef.current.removeAttribute("src");
      try { videoRef.current.load(); } catch { /* ignore */ }
    }
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    setResolvedUrl("");
    setError("تم اكتشاف أدوات المطوّر — أُغلق المشغّل لحماية المحتوى. أغلق الأدوات ثم حدّث الصفحة.");
  }, [devtoolsOpen]);

  if (devtoolsOpen) {
    return (
      <div className="relative w-full aspect-video grid place-items-center rounded-2xl border border-destructive/40 bg-black text-center p-6">
        <div>
          <ShieldCheck className="h-10 w-10 mx-auto text-destructive" />
          <p className="mt-3 font-extrabold text-white">تم تعطيل المشغّل</p>
          <p className="text-xs text-white/70 mt-1">
            رُصدت أدوات المطوّر (Devtools / Eruda). أغلقها ثم حدّث الصفحة لاستئناف المشاهدة.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        /* اجعل ووترمارك الحماية ظاهراً دائماً حتى في وضع ملء الشاشة */
        .rawanui-watermark {
          position: absolute !important;
          top: 12px;
          inset-inline-start: 12px;
          z-index: 40;
          pointer-events: none;
        }
        /* في ملء الشاشة (خاصة عند قلب الجهاز إلى العرض الأفقي) نحترم
           المناطق الآمنة (Notch / Dynamic Island) على iOS و Android
           لمنع اقتطاع أي جزء من الواترمارك خلف الفتحة. */
        :fullscreen .rawanui-watermark,
        :-webkit-full-screen .rawanui-watermark {
          top: calc(14px + env(safe-area-inset-top, 0px));
          inset-inline-start: calc(14px + env(safe-area-inset-left, 0px));
          inset-inline-end: auto;
          transform: none;
        }
      `}</style>

      <div
        ref={containerRef}
        onContextMenu={(e) => e.preventDefault()}
        className="relative overflow-hidden border border-border bg-black shadow-card select-none transition-[border-radius] duration-300 ease-in-out w-full aspect-video rounded-2xl"
        style={{ userSelect: "none" }}
      >
        {isYT ? (
          <div ref={ytHostRef} className="absolute inset-0 w-full h-full pointer-events-none" />
        ) : isEmbed ? (
          // Sandbox intentionally OMITS "allow-popups" و "allow-popups-to-escape-sandbox"
          // ↳ يمنع النقر على شعار المزوّد (Rumble/Vimeo/Facebook) من فتح أي صفحة
          //   خارجية أو تبويب جديد. المشغّل يبقى قابلاً للتشغيل داخل الإطار.
          // كذلك referrerpolicy=no-referrer لعدم كشف صفحتنا لمزوّد الفيديو.
          <iframe
            src={cleanEmbedUrl}
            className="absolute inset-0 w-full h-full border-0 bg-black z-[1]"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-presentation"
          />
        ) : (
          <video
            ref={videoRef}
            poster={poster}
            playsInline
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture={true}
            className="absolute inset-0 w-full h-full object-contain bg-black"
            onLoadedMetadata={() => videoRef.current && setDuration(videoRef.current.duration)}
            onPlay={() => setIsPaused(false)}
            onPause={() => setIsPaused(true)}
            onEnded={() => { setPhase("outro"); setTimeout(() => onEnded?.(), 3000); }}
            title={title}
          />
        )}

        {phase !== "ad" && phase !== "loading" && phase !== "intro" && phase !== "outro" && !isEmbed && (
          <div
            className="absolute inset-0 z-[2]"
            onClick={(e) => { e.stopPropagation(); showControls(); }}
            onMouseMove={showControls}
            onTouchStart={showControls}
            style={{ touchAction: "manipulation" }}
          />
        )}

        {phase !== "ad" && phase !== "loading" && phase !== "intro" && phase !== "outro" && !isEmbed && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
              showControls();
            }}
            aria-label={isPaused ? "play" : "pause"}
            className="absolute z-[3] grid place-items-center cursor-pointer"
            style={{
              top: "22.5%", bottom: "22.5%", left: "25%", right: "25%",
              background: "transparent", touchAction: "manipulation"
            }}
          >
            {isPaused && (
              <span className="grid place-items-center w-16 h-16 rounded-full bg-primary/90 text-primary-foreground shadow-2xl transition-transform active:scale-95 pointer-events-none">
                <Play className="h-8 w-8 fill-current" />
              </span>
            )}
          </button>
        )}

        {shotMsg && (
          <div
            className={`absolute top-12 left-1/2 -translate-x-1/2 z-[8] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold border ${
              shotMsgKind === "err" ? "bg-destructive/90 border-white/15" : "bg-black/80 border-white/15"
            }`}
          >
            {shotMsgKind === "err" ? (
              <AlertTriangle className="h-3.5 w-3.5" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            )}
            <span>{shotMsg}</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 z-30 grid place-items-center bg-black/80 text-center p-6">
            <div>
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-destructive" />
              <p className="text-lg font-extrabold text-destructive">{error}</p>
              <p className="text-xs text-muted-foreground mt-2">حاول التحديث أو اختر مصدراً آخر.</p>
            </div>
          </div>
        )}

        {phase === "loading" && !error && (
          <div className="absolute inset-0 z-20 grid place-items-center bg-black/70">
            <div className="text-center">
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
              <p className="mt-3 text-sm font-bold text-white">جارٍ تجهيز المشغل…</p>
            </div>
          </div>
        )}

        {phase === "intro" && (
          <div className="absolute inset-0 z-20 grid place-items-center animate-fade-in" style={{ background: "linear-gradient(135deg, #0f1419 0%, #1a3a37 50%, #299e94 100%)" }}>
            <div className="text-center">
              <div className="inline-grid place-items-center w-24 h-24 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 mb-4 animate-pulse-glow">
                <ShieldCheck className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-5xl md:text-6xl font-black text-white tracking-tight">
                Rawan<span className="text-primary">UI</span>
              </h2>
              <p className="mt-2 text-sm text-white/70 tracking-widest">SMART · SECURE · STREAMING</p>
            </div>
          </div>
        )}

        {phase === "outro" && (
          <div className="absolute inset-0 z-20 grid place-items-center animate-fade-in" style={{ background: "linear-gradient(135deg, #0f1419 0%, #1a3a37 50%, #299e94 100%)" }}>
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-black text-white">
                شكراً للمشاهدة على Rawan<span className="text-primary">UI</span>
              </h2>
              <p className="mt-3 text-sm text-white/70">Thank you for watching</p>
            </div>
          </div>
        )}

        {phase === "ad" && (
          <div className="absolute inset-0 z-20 bg-black/95 grid place-items-center animate-fade-in">
            <div className="text-center p-6 max-w-md">
              <div className="text-xs uppercase tracking-widest text-primary font-bold">إعلان · Advertisement</div>
              <h3 className="mt-3 text-3xl font-extrabold text-white">رعاة Rawan UI</h3>
              <p className="mt-2 text-sm text-white/70">يدعم استمرار الموقع — شكراً لصبرك.</p>
              <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/20 text-white font-bold">
                <Loader2 className="h-4 w-4 animate-spin" />
                يستأنف خلال {adRemain} ث
              </div>
            </div>
          </div>
        )}

        <div
          dir={isRtl ? "rtl" : "ltr"}
          className="rawanui-watermark flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white drop-shadow-lg"
        >
          {isRtl ? (
            <>
              {/* النص أولاً (يظهر يميناً في RTL) */}
              <span className="flex items-center gap-1">
                <span className="text-xs font-extrabold tracking-wide">روان</span>
                <span dir="ltr" className="text-xs font-extrabold tracking-wide text-primary">UI</span>
              </span>
              {/* الدرع ثانياً (يظهر يساراً في RTL) */}
              <ShieldCheck className="h-4 w-4 text-primary" />
            </>
          ) : (
            <>
              {/* النص أولاً (يظهر يساراً في LTR) */}
              <span className="text-xs font-extrabold tracking-wide">
                Rawan<span className="text-primary">UI</span>
              </span>
              {/* الدرع ثانياً (يظهر يميناً في LTR) */}
              <ShieldCheck className="h-4 w-4 text-primary" />
            </>
          )}
        </div>

        {/* الدروع الشفافة (Click Blockers) لمنع النقر على أزرار Iframe العلوية المزعجة */}
        {isEmbed && (
          <>
            {/* درع الزاوية اليمنى (يغطي زر القائمة والموصى بها) */}
            <div className="absolute top-0 right-0 w-[25%] h-[25%] z-[15] bg-transparent cursor-default" title="" />
            {/* درع الزاوية اليسرى (يغطي أي شعارات أو أزرار إضافية) */}
            <div className="absolute top-0 left-0 w-[25%] h-[25%] z-[15] bg-transparent cursor-default" title="" />
          </>
        )}

        {phase !== "ad" && phase !== "loading" && phase !== "intro" && phase !== "outro" && !isEmbed && (
          <div className={`absolute top-3 end-3 z-[6] transition-opacity duration-300 ${controlsVisible || isPaused ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            <button
              onClick={(e) => { e.stopPropagation(); captureScreenshot(); }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); captureScreenshot(); }}
              className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 text-white transition-all shadow-lg"
              aria-label="screenshot"
              title="التقاط لقطة"
              style={{ touchAction: "manipulation" }}
            >
              <Camera className="h-5 w-5" />
            </button>
          </div>
        )}

        {shots.length > 0 && thumbsVisible && phase !== "ad" && phase !== "loading" && phase !== "intro" && phase !== "outro" && !isEmbed && (
          <div
            onMouseEnter={() => showThumbsFor(5000)}
            onClick={(e) => { e.stopPropagation(); showThumbsFor(5000); }}
            className={`absolute z-[6] top-16 ${isRtl ? "right-3" : "left-3"} flex flex-col gap-2 max-h-[60%] overflow-y-auto hide-scrollbar animate-fade-in`}
          >
            {shots.map((s, i) => (
              <button
                key={s.id}
                onClick={(e) => { e.stopPropagation(); setOpenShot(s); showThumbsFor(5000); }}
                className="relative w-14 h-9 rounded-md overflow-hidden border-2 border-white/30 hover:border-primary shadow-lg group"
                aria-label={`shot-${i + 1}`}
                style={{ touchAction: "manipulation" }}
              >
                <img src={s.baseDataUrl} alt="" className="w-full h-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] font-extrabold text-white text-center leading-tight">{i + 1}</span>
              </button>
            ))}
          </div>
        )}

        {phase !== "ad" && phase !== "loading" && !isEmbed && (
          <div
            dir="ltr"
            onMouseMove={showControls}
            onClick={(e) => e.stopPropagation()}
            className={`absolute inset-x-0 bottom-0 z-[6] p-3 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-300 ${
              controlsVisible || isPaused ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            style={{ touchAction: "manipulation" }}
          >
            <div
              className="h-2 bg-white/20 rounded-full cursor-pointer mb-3 group"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                if (duration) seekTo(Math.max(0, Math.min(1, pct)) * duration);
              }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: duration ? `${(progress / duration) * 100}%` : "0%",
                  background: "var(--gradient-primary)",
                }}
              />
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-2 text-white">
              <button onClick={togglePlay} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="play">
                {phase === "playing" && !isPaused ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 fill-current" />}
              </button>
              
              <button onClick={() => seekBy(-10)} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="back 10s" title="-10s">
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button onClick={() => seekBy(10)} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="forward 10s" title="+10s">
                <ChevronRight className="h-5 w-5" />
              </button>
              
              <div className="flex items-center gap-1.5 group/vol">
                <button onClick={toggleMute} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="mute">
                  {muted || volume === 0
                    ? <VolumeX className="h-5 w-5" />
                    : volume < 50 ? <Volume1 className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <div className="hidden sm:flex items-center w-0 group-hover/vol:w-24 overflow-hidden transition-all duration-300 ease-in-out">
                  <div className="w-20 px-2 py-1 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full shadow-lg">
                    <Slider
                      value={[muted ? 0 : volume]}
                      min={0} max={100} step={1}
                      onValueChange={(v) => applyVolume(v[0] ?? 0)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="px-3 py-1.5 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full text-[11px] sm:text-xs font-mono font-bold tracking-wider ms-1 shadow-lg flex items-center justify-center">
                <span dir="ltr">{fmt(progress)} / {fmt(duration)}</span>
              </div>
              
              <div className="flex-1" />

              {shots.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowShotsPanel((v) => !v); setShowErrLog(false); }}
                  className={`relative p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg ${showShotsPanel ? "text-primary" : "text-white"}`}
                  aria-label="shots-gallery"
                  title="اللقطات الملتقطة"
                >
                  <Camera className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 text-[10px] bg-primary text-white rounded-full px-1.5 py-0.5 leading-none font-bold">
                    {shots.length}
                  </span>
                </button>
              )}

              {errLog.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowErrLog((s) => !s); setShowShotsPanel(false); }}
                  className={`relative p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg ${showErrLog ? "text-destructive" : "text-amber-400"}`}
                  aria-label="error-log"
                  title="سجل الأخطاء"
                >
                  <AlertTriangle className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 text-[10px] bg-destructive text-white rounded-full px-1.5 py-0.5 leading-none font-bold">
                    {errLog.length}
                  </span>
                </button>
              )}

              {isAdmin && (
                <button onClick={() => setShowAdmin((s) => !s)} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="admin">
                  <Settings className="h-5 w-5" />
                </button>
              )}
              
              <button onClick={goFullscreen} className="p-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full hover:bg-black/60 transition-all shadow-lg" aria-label="fullscreen">
                <Maximize2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {showErrLog && (
          <div className={`absolute z-[7] bottom-20 ${isRtl ? "right-3" : "left-3"} w-80 max-h-[60%] overflow-y-auto hide-scrollbar bg-black/92 border border-destructive/40 rounded-xl p-3 text-white text-xs shadow-2xl backdrop-blur`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-extrabold text-destructive">سجل الأخطاء ({errLog.length})</span>
              <div className="flex items-center gap-2">
                <button onClick={() => { setErrLog([]); writeLS(`${LS_PREFIX}errlog`, []); }}
                  className="text-[10px] underline opacity-80 hover:opacity-100">مسح الكل</button>
                <button onClick={() => setShowErrLog(false)} aria-label="close">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {errLog.length === 0 ? (
              <div className="text-white/60 text-center py-4">لا توجد أخطاء.</div>
            ) : (
              <ul className="space-y-2">
                {errLog.map((e) => (
                  <li key={e.id} className="border border-white/10 bg-white/5 rounded p-2">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-amber-300">{opLabel(e.op)}</span>
                      <span className="text-[10px] text-white/50">{new Date(e.at).toLocaleTimeString()}</span>
                    </div>
                    <div className="mt-1 text-white/80">{e.reason}</div>
                    {e.retry && (
                      <button
                        onClick={() => e.retry?.()}
                        className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-primary/20 hover:bg-primary/30 text-primary font-bold">
                        <RefreshCw className="h-3 w-3" /> إعادة المحاولة
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {showShotsPanel && shots.length > 0 && (
          <div className={`absolute z-[7] bottom-20 ${isRtl ? "right-3" : "left-3"} w-80 max-h-[60%] overflow-y-auto hide-scrollbar bg-black/92 border border-primary/40 rounded-xl p-3 text-white text-xs shadow-2xl backdrop-blur`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-extrabold text-primary">اللقطات ({shots.length})</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setShots([])} className="text-[10px] underline opacity-80 hover:opacity-100">مسح الكل</button>
                <button onClick={() => setShowShotsPanel(false)} aria-label="close">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {shots.map((s, i) => (
                <div key={s.id} className="relative group">
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpenShot(s); }}
                    className="relative w-full aspect-video rounded-md overflow-hidden border border-white/20 hover:border-primary shadow-lg"
                    aria-label={`open-shot-${i + 1}`}
                  >
                    <img src={s.baseDataUrl} alt="" className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[10px] font-mono text-white text-center">{fmt(s.time)}</span>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShots((all) => all.filter((x) => x.id !== s.id)); }}
                    className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="delete"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAdmin && isAdmin && (
          <div className="absolute top-3 end-3 z-[7] w-64 bg-black/90 border border-primary/40 rounded-xl p-3 text-white text-xs shadow-2xl backdrop-blur">
            <div className="flex items-center justify-between mb-2">
              <span className="font-extrabold text-primary">لوحة المسؤول</span>
              <button onClick={() => setShowAdmin(false)} aria-label="close"><X className="h-3.5 w-3.5" /></button>
            </div>
            <div className="space-y-2">
              <button onClick={() => triggerAd(5, -Date.now())} className="w-full px-2 py-1.5 rounded bg-primary/20 hover:bg-primary/30 flex items-center gap-2 text-start">
                <Play className="h-3.5 w-3.5 fill-current" /> بث إعلان فوري (5 ث)
              </button>
              <button onClick={() => triggerAd(10, -Date.now())} className="w-full px-2 py-1.5 rounded bg-primary/20 hover:bg-primary/30 flex items-center gap-2 text-start">
                <Play className="h-3.5 w-3.5 fill-current" /> بث إعلان فوري (10 ث)
              </button>
              <div>
                <label className="text-white/70">قفز إلى (ث):</label>
                <input
                  type="number"
                  className="w-full mt-1 px-2 py-1 rounded bg-white/10 border border-white/20 text-white"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = Number((e.target as HTMLInputElement).value);
                      if (!isNaN(v)) seekTo(v);
                    }
                  }}
                />
              </div>
              <div className="text-white/60">
                جدول الإعلانات: {schedule.length} فاصل · المخدومة: {adsServed.size}
              </div>
              <button onClick={() => { setAdsServed(new Set()); }} className="w-full px-2 py-1.5 rounded bg-white/10 hover:bg-white/20 text-start">
                <SkipForward className="h-3 w-3 inline" /> إعادة جدولة الإعلانات
              </button>
            </div>
          </div>
        )}

        {openShot && (
          isFullscreen ? (
            // وضع ملء الشاشة: نظهر الحاوية داخل المشغل لتجنب اختفائها تحت طبقة المتصفح
            <div
              className="absolute inset-0 z-[99999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
              onClick={(e) => { if (e.target === e.currentTarget) setOpenShot(null); }}
            >
              <div className="relative w-[80%] max-w-xl max-h-[90vh] overflow-y-auto hide-scrollbar bg-card border border-border rounded-xl p-4 shadow-2xl">
                <button
                  onClick={() => setOpenShot(null)}
                  className="absolute top-2 end-2 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white"
                  aria-label="close"
                >
                  <X className="h-4 w-4" />
                </button>
                <ShotEditor
                  shot={openShot}
                  composeShot={composeShot}
                  onDownload={() => downloadShot(openShot)}
                  onShare={(t) => shareShot(openShot, t)}
                  onDelete={() => { setShots((sx) => sx.filter((x) => x.id !== openShot.id)); setOpenShot(null); }}
                />
              </div>
            </div>
          ) : (
            // الوضع العادي: نظهر الحاوية خارج المشغل فوق الموقع بالكامل
            typeof document !== "undefined" && createPortal(
              <div
                className="fixed inset-0 z-[99999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
                onClick={(e) => { if (e.target === e.currentTarget) setOpenShot(null); }}
              >
                <div className="relative w-[80%] max-w-xl max-h-[90vh] overflow-y-auto hide-scrollbar bg-card border border-border rounded-xl p-4 shadow-2xl">
                  <button
                    onClick={() => setOpenShot(null)}
                    className="absolute top-2 end-2 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white"
                    aria-label="close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <ShotEditor
                    shot={openShot}
                    composeShot={composeShot}
                    onDownload={() => downloadShot(openShot)}
                    onShare={(t) => shareShot(openShot, t)}
                    onDelete={() => { setShots((sx) => sx.filter((x) => x.id !== openShot.id)); setOpenShot(null); }}
                  />
                </div>
              </div>,
              document.body
            )
          )
        )}
      </div>
    </>
  );
}

function opLabel(op: ErrLogItem["op"]) {
  switch (op) {
    case "save": return "حفظ";
    case "share-native": return "مشاركة (نظام)";
    case "share-whatsapp": return "واتساب";
    case "share-telegram": return "تلجرام";
    case "capture": return "التقاط";
    case "compose": return "تركيب صورة";
  }
}

function ShotEditor({
  shot, composeShot, onDownload, onShare, onDelete,
}: {
  shot: Shot;
  composeShot: (s: { baseDataUrl: string; W: number; H: number; time: number }) => Promise<string>;
  onDownload: () => void;
  onShare: (t: "whatsapp" | "telegram" | "native") => void;
  onDelete: () => void;
}) {
  const [preview, setPreview] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    composeShot(shot).then((u) => { if (!cancelled) setPreview(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [shot, composeShot]);

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden border border-border shadow-2xl">
        <img src={preview || shot.baseDataUrl} alt="screenshot preview" className="w-full" />
      </div>
      
      <div className="flex flex-wrap gap-2 justify-center pt-2">
        <button onClick={onDownload} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-bold hover:opacity-90">
          <Download className="h-4 w-4" /> تنزيل
        </button>
        <button onClick={() => onShare("native")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border font-bold hover:bg-accent">
          <Share2 className="h-4 w-4" /> مشاركة
        </button>
        <button onClick={() => onShare("whatsapp")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25D366] text-white font-bold hover:opacity-90">
          WhatsApp
        </button>
        <button onClick={() => onShare("telegram")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#229ED9] text-white font-bold hover:opacity-90">
          Telegram
        </button>
        <button onClick={onDelete} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive/15 text-destructive font-bold hover:bg-destructive/25">
          <X className="h-4 w-4" /> حذف
        </button>
      </div>
    </div>
  );
}
